import { useMutation } from '@tanstack/react-query';
import { publicFormApi } from '../api/publicForms';
import { useCallback } from 'react';
import type { Question, ValidationResult, FormState } from '../types/publicForm.types';

// Client-side validation functions
const validateEmail = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

const validateUrl = (value: string): boolean => {
  return /^https?:\/\/.+/.test(value);
};

const validateNumber = (value: string): boolean => {
  return /^-?\d+(\.\d+)?$/.test(value);
};

const hasValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object' && 'answerId' in value) return !!value.answerId;
  if (typeof value === 'object' && 'answerIds' in value) return value.answerIds.length > 0;
  if (typeof value === 'object' && 'fileKey' in value) return !!value.fileKey;
  return !!value;
};

export const usePageValidation = (formId: string, formState: FormState) => {
  // Client-side validation
  const validateQuestion = useCallback((question: Question, value: any): string | null => {
    // Skip conditional questions that aren't visible
    if (question.parentAnswerId && !formState.visibleChildQuestions.has(question.id)) {
      return null;
    }

    // Required field validation
    if (question.required && !hasValue(value)) {
      return 'This field is required';
    }

    // Skip further validation if no value
    if (!hasValue(value)) return null;

    // Type-specific validation
    switch (question.answerStyle) {
      case 'email':
        if (!validateEmail(value)) {
          return 'Please enter a valid email address';
        }
        break;
      
      case 'url':
        if (!validateUrl(value)) {
          return 'Please enter a valid URL';
        }
        break;
      
      case 'number':
        if (!validateNumber(value)) {
          return 'Please enter a valid number';
        }
        break;
      
      case 'text':
      case 'textarea':
        // Character limit validation
        if (question.options?.useCharacterLimit && question.options.characterLimit) {
          if (value.length > question.options.characterLimit) {
            return `Must be ${question.options.characterLimit} characters or less`;
          }
        }
        break;
    }

    // Comment validation if required
    if (question.showComments && question.required) {
      const commentValue = formState.formData[`${question.id}_comment`];
      if (!commentValue || commentValue.trim().length === 0) {
        return 'Please provide a comment';
      }
    }

    return null;
  }, [formState.visibleChildQuestions, formState.formData]);

  // Validate all questions on a page
  const validatePage = useCallback((questions: Question[]): ValidationResult => {
    const errors: Record<string, string> = {};
    let isValid = true;

    questions.forEach(question => {
      const value = formState.formData[question.id];
      const error = validateQuestion(question, value);

      if (error) {
        errors[question.id] = error;
        isValid = false;
      }
    });

    return { isValid, errors };
  }, [formState.formData, validateQuestion]);

  // Server-side validation mutation
  const validatePageMutation = useMutation({
    mutationFn: async ({ pageIndex, responses }: { pageIndex: number; responses: any }) => {
      return publicFormApi.validatePage(formId, pageIndex, responses);
    },
  });

  // Combined validation (client + server)
  const validatePageWithServer = useCallback(async (
    questions: Question[],
    pageIndex: number
  ): Promise<ValidationResult> => {
    // First do client-side validation
    const clientValidation = validatePage(questions);
    
    if (!clientValidation.isValid) {
      return clientValidation;
    }

    // Then do server-side validation
    try {
      const serverValidation = await validatePageMutation.mutateAsync({
        pageIndex,
        responses: formState.formData,
      });

      return {
        isValid: serverValidation.valid,
        errors: serverValidation.errors || {},
      };
    } catch (error) {
      // If server validation fails, allow progression with warning
      console.error('Server validation failed:', error);
      return { isValid: true, errors: {} };
    }
  }, [validatePage, validatePageMutation, formState.formData]);

  return {
    validateQuestion,
    validatePage,
    validatePageWithServer,
    isValidating: validatePageMutation.isPending,
  };
};